import axios from "axios";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import ApiUrl from "./../Environment/ApiUrl";
import { useToast } from "../Toast/ToastContext";

function ChitListPaymentHistory() {
  const navigate = useNavigate();
  const toast = useToast();
  const { customer_id } = useParams();
  const [chitdata, setChitdata] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(null);

  const fetchChitListData = async () => {
    try {
      const response = await axios.get(
        `${ApiUrl}/Api/chit_list.php?customer_id=${customer_id}`
      );
      console.log("API Response:", response.data);

      if (response.data.data && response.data.data.length > 0) {
        setChitdata(response.data.data[0]);
      } else {
        console.error("No chit data found in response");
        setError("No chit data found");
        toast.error("No chit data found", 3000, "topRight");
      }
    } catch (error) {
      console.error("Error fetching chit data:", error);
      const errorMsg = error.response?.data?.message || error.message;
      setError("Failed to load chit data: " + errorMsg);
      toast.error("Failed to load chit data: " + errorMsg, 3000, "topRight");
    } finally {
      setLoading(false);
    }
  };

  const deletePaymentHistory = async (paymentId, index) => {
    if (!window.confirm("Are you sure you want to delete this payment record?")) {
      return;
    }

    setDeleteLoading(index);

    try {
      const response = await axios.delete(
        `${ApiUrl}/Api/chit_list_payement_history.php`,
        {
          data: {
            payment_id: paymentId,
            chit_id: customer_id,
          },
        }
      );

      console.log("Delete Response:", response.data);

      if (response.data.success || response.status === 200) {
        // Update local state
        setChitdata((prevData) => ({
          ...prevData,
          payment_history: prevData.payment_history.filter(
            (_, i) => i !== index
          ),
        }));
        
        toast.success("Payment record deleted successfully!", 3000, "topRight");
      } else {
        throw new Error(response.data.message || "Failed to delete payment record");
      }
    } catch (error) {
      console.error("Error deleting payment:", error);
      const errorMsg = error.response?.data?.message || error.message;
      toast.error("Failed to delete payment record: " + errorMsg, 3000, "topRight");
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleback = () => {
    navigate("/chit/chit-list");
  };

  useEffect(() => {
    if (customer_id) {
      fetchChitListData();
    }
  }, [customer_id]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "50vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !chitdata) {
    return (
      <div className="container-fluid py-3">
        <div className="alert alert-danger text-center">
          {error || "No chit data available"}
        </div>
        <div className="text-center">
          <button className="btn btn-primary btn-sm" onClick={handleback}>
            Back to Chit List
          </button>
        </div>
      </div>
    );
  }

  // Calculate Paid, Total, Balance
  const totalAmount = parseFloat(chitdata.amount) * parseInt(chitdata.duration || 1);
  const paidAmount = chitdata.payment_history?.reduce((sum, p) => {
    return sum + (parseFloat(p.amount || p.paid) || 0);
  }, 0) || 0;
  const balanceAmount = totalAmount - paidAmount;
  const paymentProgress = totalAmount > 0 ? (paidAmount / totalAmount) * 100 : 0;

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  return (
    <div className="container-fluid py-3 mb-5">
      {/* Back Button */}
      <div className="mb-3">
        <button className="btn btn-secondary btn-sm" onClick={handleback}>
          <i className="fa-solid fa-arrow-left me-2"></i> Back to Chit List
        </button>
      </div>

      {/* Chit Customer Details and Payment Summary Row */}
      <div className="row g-3 p-3">
  {/* Chit Customer Details Card */}
  <div className="col-md-6">
    <div className="card p-3 h-100" style={{ borderRadius: "15px" }}>
      <h6 className="text-primary text-center mb-3">Chit Customer Details</h6>
      <div className="row g-2">
        <div className="col-md-4">
          <label className="form-label fw-bold">Customer ID</label>
          <input
            className="form-control bg-light"
            value={chitdata.customer_id || "N/A"}
            readOnly
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">Customer Name</label>
          <input
            className="form-control bg-light"
            value={chitdata.name || "N/A"}
            readOnly
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">Chit ID</label>
          <input
            className="form-control bg-light"
            value={chitdata.chit_id || "N/A"}
            readOnly
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">Duration</label>
          <input
            className="form-control bg-light"
            value={`${chitdata.duration || "N/A"} Months`}
            readOnly
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">Total Amount</label>
          <input
            className="form-control bg-light"
            value={`₹ ${formatCurrency(totalAmount)}`}
            readOnly
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">Start Date</label>
          <input
            className="form-control bg-light"
            value={
              chitdata.start_date
                ? new Date(chitdata.start_date).toLocaleDateString()
                : "N/A"
            }
            readOnly
          />
        </div>
      </div>
    </div>
  </div>

  {/* Payment Summary Card */}
  <div className="col-md-6">
    <div className="card p-3 bg-light h-100" style={{ borderRadius: "15px" }}>
      <h6 className="text-success text-center mb-3">Payment Summary</h6>
      <div className="row g-2">
        <div className="col-md-4">
          <label className="form-label fw-bold">Total Amount</label>
          <input
            className="form-control bg-white"
            value={`₹ ${formatCurrency(totalAmount)}`}
            readOnly
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold text-success">Paid Amount</label>
          <input
            className="form-control bg-white text-success fw-bold"
            value={`₹ ${formatCurrency(paidAmount)}`}
            readOnly
          />
        </div>
        <div className="col-md-4">
          <label className="form-label fw-bold">Balance Amount</label>
          <input
            className="form-control bg-white"
            value={`₹ ${formatCurrency(balanceAmount)}`}
            readOnly
            style={{ color: balanceAmount > 0 ? "red" : "green", fontWeight: "bold" }}
          />
        </div>
      </div>
      
      {/* Progress Bar */}
      {totalAmount > 0 && (
        <div className="row mt-3">
          <div className="col-12">
            <div className="progress" style={{ height: "10px" }}>
              <div 
                className="progress-bar bg-success" 
                style={{ width: `${(paidAmount / totalAmount) * 100}%` }}
                role="progressbar"
              ></div>
            </div>
            <small className="text-muted mt-1 d-block text-center">
              {((paidAmount / totalAmount) * 100).toFixed(1)}% completed
            </small>
          </div>
        </div>
      )}
    </div>
  </div>
</div>

      {/* Payment History Table */}
      <div className="px-3">
        <div className="card shadow-lg p-4 rounded-3" style={{ backgroundColor: "#ffffff" }}>
        <h6 className="text-center mb-4 fw-bold text-dark">Payment History</h6>
        <div className="table-responsive">
          <table className="table table-striped table-bordered align-middle">
            <thead className="table-light">
              <tr>
                <th scope="col" className="px-3 py-2">S.No</th>
                <th scope="col" className="px-3 py-2">Date</th>
                <th scope="col" className="px-3 py-2">Pay Type</th>
                <th scope="col" className="px-3 py-2">Paid Amount (₹)</th>
                <th scope="col" className="px-3 py-2">Action</th>
              </tr>
            </thead>
            <tbody>
              {chitdata?.payment_history?.length > 0 ? (
                chitdata.payment_history.map((payment, index) => (
                  <tr key={index}>
                    <td className="px-3 py-2 text-center">{index + 1}</td>
                    <td className="px-3 py-2">
                      {payment.payment_date
                        ? new Date(payment.payment_date).toLocaleDateString()
                        : "N/A"}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`badge ${payment.payment_type === "Cash" ? "bg-success" : "bg-info"}`}>
                        {payment.payment_type || "N/A"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-end fw-bold">
                      ₹ {formatCurrency(parseFloat(payment.paid || payment.amount || 0))}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        className="btn btn-sm btn-light"
                        onClick={() => deletePaymentHistory(payment.id || payment.payment_id, index)}
                        disabled={deleteLoading === index}
                      >
                        {deleteLoading === index ? (
                          <span className="spinner-border spinner-border-sm" role="status"></span>
                        ) : (
                          <i className="fa fa-trash text-danger fs-6"></i>
                        )}
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center py-4 text-muted">
                    <i className="fa fa-info-circle me-2"></i> No payment history available
                  </td>
                </tr>
              )}
            </tbody>
            {chitdata?.payment_history?.length > 0 && (
              <tfoot className="table-secondary">
                <tr>
                  <th colSpan="3" className="text-end fw-bold">Total Paid:</th>
                  <th className="text-end fw-bold">₹ {formatCurrency(paidAmount)}</th>
                  <th></th>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
        <div className="d-flex justify-content-center gap-2 mt-4">
          <button onClick={handleback} className="btn btn-primary btn-sm px-4">
            <i className="fa-solid fa-arrow-left me-2"></i> Back
          </button>
        </div>
      </div>
      </div>
    </div>
  );
}

export default ChitListPaymentHistory;